import { Directive, HostBinding, input } from '@angular/core';
import { AbstractControl } from '@angular/forms';

/**
 * an attribute directive that adds CSS classes to a dirty host element(s) related to the validity of a FormControl or a group of FormControls
 *
 * @example
 * <div class="form-group has-feedback" [formGroup]="form" [ishShowFormFeedback]="formControl">
 *               <input
 *                 [type]="type"
 *                 class="form-control"
 *                 [formControlName]="controlName">
 * </div>
 *
 * <div class="form-group has-feedback" [formGroup]="form" [ishShowFormFeedback]="[formControl, formControl2]">
 *               <input
 *                 [type]="type"
 *                 class="form-control"
 *                 [formControlName]="controlName">
 *
 *                <input
 *                 [type]="type"
 *                 class="form-control2"
 *                 [formControlName]="controlName2">
 * </div>
 */
@Directive({ selector: '[ishShowFormFeedback]' })
export class ShowFormFeedbackDirective {
  /**
   * FormControl which validation status is considered
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly control = input<AbstractControl | AbstractControl[]>(undefined, { alias: 'ishShowFormFeedback' });

  /**
   *  If form control is invalid and dirty 'has-error' class is added
   */
  @HostBinding('class.has-error')
  get hasErrors() {
    return this.determineErrors();
  }

  /**
   *  If form control is valid and dirty 'has-success' class is added
   */
  @HostBinding('class.has-success')
  get hasSuccess() {
    const controlValue = this.control();
    if (controlValue instanceof AbstractControl) {
      return controlValue.validator && controlValue.valid && controlValue.dirty;
    } else if (Array.isArray(controlValue)) {
      if (this.determineErrors()) {
        return false;
      }
      return controlValue.every(control => control.validator && control.valid && control.dirty);
    }
  }

  private determineErrors(): boolean {
    const controlValue = this.control();
    if (controlValue instanceof AbstractControl) {
      return controlValue.validator && controlValue.invalid && controlValue.dirty;
    } else if (Array.isArray(controlValue)) {
      return controlValue.some(control => control.validator && control.invalid && control.dirty);
    }
  }
}
